import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Revert } from 'jbc-front/components/icons';
import LoadingView from 'src/components/LoadingView';
import { TextField } from 'src/components/Form';
import { toDisplayFormat } from 'src/utils/Completion';
import { numberWithComma } from 'src/utils/Utils';
import hintStyles from 'jbc-front/components/Hint.scss';
import { isThaiMoney } from 'src/utils/CustomValidators';
import styles from './TaxDeductionCertsTable.scss';

const TaxDeductionCertsTable = ({
  isListLoading,
  employeePays,
  isEditMode,
  handleSubmit,
  reset,
  onSetResetForm,
  onSetHandleSubmit,
  unconfirmedMonths,
  onReset
}) => {
  const { t } = useTranslation();
  const [tooltipInfo, setTooltipInfo] = useState({ visible: false, x: 0, y: 0, content: '' });

  const handleMouseEnter = (e, content) => {
    if (content) {
      setTooltipInfo({ visible: true, x: e.clientX, y: e.clientY, content });
    }
  };

  const handleMouseMove = (e, content) => {
    if (content) {
      setTooltipInfo({ visible: true, x: e.clientX, y: e.clientY, content });
    }
  };

  const handleMouseLeave = () => {
    setTooltipInfo({ visible: false, x: 0, y: 0, content: '' });
  };

  useEffect(
    () => {
      if (onSetResetForm) onSetResetForm(reset);
    },
    [reset]
  );

  useEffect(
    () => {
      if (onSetHandleSubmit) onSetHandleSubmit(handleSubmit);
    },
    [handleSubmit]
  );

  const hasAnyAdjustment = employeePays && employeePays.some(employee => employee.hasAdjustment);
  const showActionColumn = hasAnyAdjustment && !isEditMode;

  const normalizeNumber = value => (value ? value.replace(/[^\d.]/g, '') : value);

  const renderEditableCell = (employee, field) => (
    <td className={`${isEditMode ? styles.inputNumberRow : ''} ${styles.numberColumn}`}>
      {isEditMode ? (
        <TextField className={styles.hideErrorField} name={`emp_${employee.employeeId}.${field}`} noLabel textAlign="right" validate={isThaiMoney} normalize={normalizeNumber} />
      ) : (
        toDisplayFormat(numberWithComma(employee[field]))
      )}
    </td>
  );

  return (
    <LoadingView isLoading={isListLoading}>
      <form id="taxDeductionCertEditForm" onSubmit={handleSubmit}>
        <div className={styles.dataTable}>
          <table className={`m-table-list ${styles.table}`}>
            <thead>
              <tr className={styles.sectionHeader}>
                <th colSpan={4} className={styles.boldBorderRight}>
                  {t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.section_i_title')}
                </th>
                <th colSpan={8} className={`${styles.boldBorderRight} ${styles.boldBorderLeft}`}>
                  {t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.section_ii_title')}
                </th>
                <th colSpan={7} className={styles.boldBorderLeft}>
                  {t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.section_iii_title')}
                </th>
                {showActionColumn && <th rowSpan={2} className={`${styles.actionColumn} ${styles.boldBorderLeft}`} />}
              </tr>
              <tr>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.company_name')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.company_tax_number')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.company_address')}</th>
                <th className={styles.boldBorderRight}>
                  {t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.company_phone_number')}
                </th>
                <th className={styles.boldBorderLeft}>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.employee_name')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.employee_tax_number')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.nationality')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.resident_individual')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.non_resident_individual')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.employee_address')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.employee_phone_number')}</th>
                <th className={styles.boldBorderRight}>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.personal_number')}</th>
                <th className={styles.boldBorderLeft}>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.income_type')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.total_sso_amount')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.total_charity_amount')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.time_of_income_payment')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.total_assessable_income')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.total_taxable_income')}</th>
                <th>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.total_income_tax')}</th>
              </tr>
            </thead>
            <tbody>
              {(!employeePays || employeePays.length === 0) && (
                <tr className={styles.no_data}>
                  <td>{t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.no_data')}</td>
                </tr>
              )}
              {employeePays.map(employee => {
                const empUnconfirmed = unconfirmedMonths && unconfirmedMonths[employee.employeeId];
                const hasUnconfirmed = empUnconfirmed && empUnconfirmed.length > 0;
                const rowClassName = hasUnconfirmed ? `${styles.row} ${styles.unconfirmedRow}` : `${styles.row}`;

                const tooltipContent = hasUnconfirmed
                  ? t('features.YearEndReportTaxDeductionCerts.components.TaxDeductionCertsTable.unconfirmed_warning', { months: empUnconfirmed.join(', ') })
                  : undefined;

                return (
                  <tr
                    key={employee.employeeId}
                    className={rowClassName}
                    onMouseEnter={e => handleMouseEnter(e, tooltipContent)}
                    onMouseMove={e => handleMouseMove(e, tooltipContent)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <td>{employee.companyName}</td>
                    <td>{employee.companyTaxNumber}</td>
                    <td>{employee.companyAddress}</td>
                    <td className={styles.boldBorderRight}>{employee.companyPhoneNumber}</td>
                    <td className={styles.boldBorderLeft}>{employee.employeeName}</td>
                    <td>{employee.employeeTaxNumber}</td>
                    <td>{employee.nationality}</td>
                    <td>{employee.residentIndividual ? <Check size={16} className={styles.check} /> : '-'}</td>
                    <td>{employee.nonResidentIndividual ? <Check size={16} className={styles.check} /> : '-'}</td>
                    <td>{employee.employeeAddress}</td>
                    <td>{employee.employeePhoneNumber}</td>
                    <td className={styles.boldBorderRight}>{employee.personalNumber}</td>
                    <td className={styles.boldBorderLeft}>{employee.incomeType}</td>
                    {renderEditableCell(employee, 'totalSsoAmount')}
                    {renderEditableCell(employee, 'totalCharityAmount')}
                    <td>{employee.timeOfIncomePayment}</td>
                    {renderEditableCell(employee, 'totalAssessableIncome')}
                    {renderEditableCell(employee, 'totalTaxableIncome')}
                    {renderEditableCell(employee, 'totalWithholdingTaxAmount')}
                    {showActionColumn && (
                      <td className={`${styles.actionColumn} ${styles.boldBorderLeft}`}>
                        {employee.hasAdjustment && (
                          <span role="button" tabIndex={0} className={styles.resetButton} onClick={() => onReset(employee.employeeId)}>
                            <Revert size={17} />
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {tooltipInfo.visible && (
          <div className={`${hintStyles.hintBaloon} ${styles.customHintBaloon}`} style={{ top: tooltipInfo.y - 25, left: tooltipInfo.x }}>
            <div className={`${hintStyles.hintShape} ${styles.customHintShape}`} />
            <div className={`${hintStyles.hintMessage} ${styles.customHintMessage}`}>{tooltipInfo.content}</div>
          </div>
        )}
      </form>
    </LoadingView>
  );
};

export default TaxDeductionCertsTable;
